import React from 'react';
import { Box, Button, Stack } from '@mui/material';
import { CircleDeleteIcon } from '@/theme/overrides/CustomIcons';
import LegacyFileCard, { LegacyFileCardStatus } from './LegacyFileCard';

export type PdfPreviewProps = {
  readonly name: string;
  readonly link?: string;
  readonly status?: LegacyFileCardStatus;
  readonly onRemove?: () => void;
};

/**
 * PDF best-effort 미리보기 (품의 코멘트 전용).
 * - 업로드 중/실패 시에는 LegacyFileCard 로 표시 (미리보기 불가).
 * - 성공 시 <object> 로 인라인 렌더. 렌더 불가(모바일/정책 차단) 시 children(fallback 카드) 자동 노출.
 * - 컨테이너는 고정폭 + 세로 리사이즈. 다운로드 attr 없음.
 */
const PdfPreview = ({
  name,
  link,
  status = 'success',
  onRemove,
}: PdfPreviewProps) => {
  if (status !== 'success' || !link) {
    return (
      <LegacyFileCard
        name={name}
        link={link}
        status={status}
        onRemove={onRemove}
      />
    );
  }

  const openInNewWindow = () => {
    if (!link) return;
    window.open(link, '_blank', 'noopener');
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '560px',
        border: '1px solid var(--dk-line, #E7E9EC)',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: '6px',
          padding: '6px 8px',
          borderBottom: '1px solid var(--dk-line, #E7E9EC)',
        }}
      >
        <Box
          component="span"
          onClick={openInNewWindow}
          title={name}
          className="ellipsis no-drag"
          sx={{
            color: 'var(--dk-accent, #0066cc)',
            textDecoration: 'underline',
            cursor: 'pointer',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flex: 1,
          }}
        >
          {name}
        </Box>
        {onRemove && (
          <Button
            disableRipple
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            sx={{
              minWidth: 'auto',
              width: '20px',
              height: '20px',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <CircleDeleteIcon />
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          width: '100%',
          height: '420px',
          minHeight: '160px',
          resize: 'vertical',
          overflow: 'auto',
        }}
      >
        <object
          data={link}
          type="application/pdf"
          style={{ width: '100%', height: '100%' }}
        >
          {/* 브라우저가 PDF 렌더 불가 시 자동 노출되는 fallback 카드 */}
          <Box sx={{ padding: '8px' }}>
            <LegacyFileCard name={name} link={link} status="success" />
          </Box>
        </object>
      </Box>
    </Box>
  );
};

export default PdfPreview;
